import { useCallback } from 'react';
import { useAppSelector } from '@hooks/redux';
import { selectAuthView } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';
import {
  QueryEvent,
  QueryItem,
  UpdateQueryRequest,
} from '@restore/queries/queries.types';

type SubmitData = {
  username: string | null | undefined;
  password: string | null | undefined;
  signAgreement: boolean | null | undefined;
  message: string;
  attachments: File[];
};

type UseQCQueryHandlersProps = {
  ctId: string;
  queryId?: string;
  currentAction?: QueryEvent | null;
  updating: boolean;
  updateCurrentQuery: (payload: {
    clinicalTrialId: string;
    queryId: string;
    siteId?: string;
    updateData: UpdateQueryRequest;
    refreshList?: boolean;
  }) => void;
};

export const useQCQueryHandlers = ({
  ctId,
  queryId,
  currentAction,
  updating,
  updateCurrentQuery,
}: UseQCQueryHandlersProps) => {
  const { selectedRole, uuid } = useAppSelector(selectAuthView);

  const handleListQuerySubmit = useCallback(
    (event: QueryEvent, query: QueryItem, data: SubmitData) => {
      if (!ctId || !uuid || !query.id) return;

      updateCurrentQuery({
        clinicalTrialId: ctId,
        queryId: query.id,
        updateData: {
          event,
          actedBy: uuid,
          message: data.message,
          attachments: data.attachments ?? [],
          ...(data.username &&
            data.password &&
            data.signAgreement && {
              userCredentials: {
                username: data.username,
                password: data.password,
                signAgreement: data.signAgreement,
              },
            }),
        },
        refreshList: true,
      });
    },
    [ctId, uuid, updateCurrentQuery]
  );

  const handleUpdateCurrentQuery = useCallback(
    (updateData: UpdateQueryRequest) => {
      if (!ctId || !queryId) return;
      updateCurrentQuery({
        clinicalTrialId: ctId,
        queryId: queryId as string,
        updateData,
      });
    },
    [ctId, queryId, updateCurrentQuery]
  );

  const handleDetailQuerySubmit = useCallback(
    (data: SubmitData) => {
      if (!currentAction || !ctId || !queryId) return;
      updateCurrentQuery({
        clinicalTrialId: ctId,
        queryId,
        updateData: {
          event: currentAction,
          actedBy: uuid as string,
          message: data.message,
          attachments: data.attachments ?? [],
          ...(data.username &&
            data.password &&
            data.signAgreement && {
              userCredentials: {
                username: data.username,
                password: data.password,
                signAgreement: data.signAgreement,
              },
            }),
        },
      });
    },
    [ctId, queryId, currentAction, uuid, updateCurrentQuery]
  );

  return {
    handlers: {
      selectedRole: selectedRole as UserRoles,
      handleListQuerySubmit,
      updating,
    },
    handleUpdateCurrentQuery,
    handleDetailQuerySubmit,
  };
};
